import api from './api';

export interface ReportOverview {
  totalPagamentos: number;
  receitaTotal: number;
  clientesActivos: number;
  taxaSucesso: number;
  pagamentosSucesso: number;
  pagamentosPendentes: number;
  pagamentosFalhados: number;
}

export interface PaymentOverTimeData {
  month: string;
  payments: number;
  revenue: number;
}

export interface EntityPaymentData {
  name: string;
  value: number;
  amount: number;
  count: number;
}

export interface PaymentMethodData {
  name: string;
  value: number;
  count: number;
  amount: number;
}

export interface DailyTransactionData {
  day: string;
  count: number;
  value: number;
}

export interface PlatformData {
  name: string;
  value: number;
  count: number;
  amount: number;
}

export interface OriginData {
  name: string;
  value: number;
  count: number;
  amount: number;
}

export type ReportPeriod = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'all' | 'custom';

// ─── Diagnóstico de falhas de pagamento ──────────────────────────────────────

export interface FalhaServico {
  servico: string;
  tentativas: number;
  pagos: number;
  falhados: number;
  expirados: number;
  taxa_sucesso: number;
  valor_pago: number;
  valor_perdido: number;
}

export interface FalhaMotivo {
  servico: string;
  codigo: string;
  motivo: string;
  n: number;
  valor: number;
}

export interface FalhaEntidade {
  entidade: string;
  nome: string;
  tentativas: number;
  pagos: number;
  taxa_sucesso: number;
}

export interface FalhaEvolucao {
  mes: string;
  tentativas: number;
  pagos: number;
  taxa_sucesso: number;
}

export interface DiagnosticoFalhas {
  dias: number;
  totais: {
    tentativas: number;
    pagos: number;
    falhados: number;
    expirados: number;
    valor_perdido: number;
    taxa_sucesso: number;
  };
  cobertura: { com_motivo: number; total: number; pct: number };
  por_servico: FalhaServico[];
  motivos: FalhaMotivo[];
  por_entidade: FalhaEntidade[];
  evolucao: FalhaEvolucao[];
}

class ReportsService {
  async getDiagnosticoFalhas(dias = 90, servico?: string): Promise<DiagnosticoFalhas> {
    const params = new URLSearchParams({ dias: String(dias) });
    if (servico) params.set('servico', servico);
    const response = await api.get(`/admin/reports/falhas?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get overview statistics
   */
  async getOverview(period: ReportPeriod = 'month', startDate?: string, endDate?: string): Promise<{ success: boolean; data: ReportOverview }> {
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api.get(`/admin/reports/overview?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get overview error:', error);
      throw error;
    }
  }

  /**
   * Get payments over time
   */
  async getPaymentsOverTime(period: ReportPeriod = 'all'): Promise<{ success: boolean; data: PaymentOverTimeData[] }> {
    try {
      const response = await api.get(`/admin/reports/payments-over-time?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Get payments over time error:', error);
      throw error;
    }
  }

  /**
   * Get payments by entity
   */
  async getPaymentsByEntity(period: ReportPeriod = 'month'): Promise<{ success: boolean; data: EntityPaymentData[] }> {
    try {
      const response = await api.get(`/admin/reports/payments-by-entity?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Get payments by entity error:', error);
      throw error;
    }
  }

  /**
   * Get payments by method
   */
  async getPaymentsByMethod(period: ReportPeriod = 'month'): Promise<{ success: boolean; data: PaymentMethodData[] }> {
    try {
      const response = await api.get(`/admin/reports/payments-by-method?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Get payments by method error:', error);
      throw error;
    }
  }

  /**
   * Get daily transactions (last 7 days)
   */
  async getDailyTransactions(): Promise<{ success: boolean; data: DailyTransactionData[] }> {
    try {
      const response = await api.get('/admin/reports/daily-transactions');
      return response.data;
    } catch (error) {
      console.error('Get daily transactions error:', error);
      throw error;
    }
  }

  /**
   * Get payments by platform
   */
  async getByPlatform(period: ReportPeriod = 'month'): Promise<{ success: boolean; data: PlatformData[] }> {
    try {
      const response = await api.get(`/admin/reports/by-platform?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Get by platform error:', error);
      throw error;
    }
  }

  /**
   * Get payments by origin
   */
  async getByOrigin(period: ReportPeriod = 'month'): Promise<{ success: boolean; data: OriginData[] }> {
    try {
      const response = await api.get(`/admin/reports/by-origin?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Get by origin error:', error);
      throw error;
    }
  }
}

export default new ReportsService();
